/**
 * One value cut to its container with an ellipsis, `lines` lines deep. The full value is always in
 * the document, so a screen reader reads all of it. Only when the value really is cut does it take a
 * tab stop and show the full text on hover and on keyboard focus, so a short value costs the person
 * nothing.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { Fragment, useEffect, useId, useRef, useState } from "react";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import { focusRing } from "./focus.ts";
import { Tooltip } from "./Tooltip.tsx";
const styles = {
  one: {
    k1xSpc: "x1lliihq",
    kAzted: "xv75w1r",
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    khDVqt: "xuxw1ft",
    $$css: true
  },
  many: {
    k1xSpc: "x104kibb",
    k7Eaqz: "xeuugli",
    kVQacm: "xb3r6kr",
    kgKLqz: "x1ua5tub",
    kHjlTd: "xj0a0fe",
    $$css: true
  }
};

/**
 * An email breaks after its "@" and its dots, not in the middle of a word. Only where lines may
 * wrap: a `<wbr>` lets even a one-line value wrap, so it would never be cut.
 */
function breakAtEmailParts(value: string): ReactNode {
  if (!value.includes("@") || value.includes(" ")) return value;
  return value.split(/(?<=[@._-])/).map((part, at) => <Fragment key={at}>
      {at > 0 ? <wbr /> : null}
      {part}
    </Fragment>);
}
export function Truncate({
  value,
  lines = 1,
  testId
}: {
  readonly value: string;
  readonly lines?: number;
  readonly testId?: string;
}) {
  const id = useId();
  const box = useRef<HTMLSpanElement>(null);
  const [cut, setCut] = useState(false);
  useEffect(() => {
    const element = box.current;
    if (!element) return;
    const measure = () => setCut(element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [value, lines]);
  return <Tooltip id={id} text={value} fill off={!cut}>
      <span ref={box} tabIndex={cut ? 0 : undefined}
    /* A focusable element needs a role to be named, and its name is all of the value. */ role={cut ? "group" : undefined} aria-label={cut ? value : undefined} data-testid={testId} data-cut={cut} style={lines > 1 ? {
      WebkitLineClamp: lines
    } : undefined} {...stylex.props(lines > 1 ? styles.many : styles.one, cut && focusRing)}>
        {lines > 1 ? breakAtEmailParts(value) : value}
      </span>
    </Tooltip>;
}